import api from '../api'
import store from '../store'
import { fetchUser, clearBackupEmailInStore } from './user'
import {
  isEmailVerificationQAEnabled,
  getEmailVerificationQAState,
  setEmailVerificationQAState,
  derivePrompts,
  fakeVerificationRecord,
  reauthRequiredError,
  mockVerificationCodeError,
  mockRequestDelay,
} from '../utils/emailVerificationQA'

export function fetchEmailVerificationPrompts(userId) {
  if (isEmailVerificationQAEnabled()) {
    return (dispatch) => mockRequestDelay().then(() => {
      const prompts = derivePrompts(getEmailVerificationQAState())
      dispatch({ type: 'FETCH_EMAIL_VERIFICATION_PROMPTS_SUCCESS', response: prompts })
      return prompts
    })
  }
  return (dispatch) => api.fetch(`/users/${userId}/email-verification/prompts`)
    .then((response) => {
      dispatch({ type: 'FETCH_EMAIL_VERIFICATION_PROMPTS_SUCCESS', response })
      return response
    })
    .catch(() => {})
}

// mirrors the real user.meta.emailVerificationPromptState.addBackupDismissCount
// so QA mode's "Don't ask me again" threshold is testable by actually
// clicking "Not now", not just via the QA panel's manual override
function patchMockAddBackupDismissCount(dispatch, addBackupDismissCount) {
  const currentUser = store.getState().user
  if (!currentUser) return
  dispatch({
    type: 'UPDATE_USER_SUCCESS',
    response: {
      ...currentUser,
      meta: {
        ...currentUser.meta,
        emailVerificationPromptState: {
          ...(currentUser.meta && currentUser.meta.emailVerificationPromptState),
          addBackupDismissCount,
        },
      },
    },
  })
}

// name = 'verifyPrimary' | 'affirmPrimary' | 'addBackup' | 'affirmBackup'
// data = { never?: true } — never: true means "don't ask me again" for this prompt
export function dismissEmailVerificationPrompt(userId, name, data) {
  if (isEmailVerificationQAEnabled()) {
    return (dispatch) => mockRequestDelay().then(() => {
      if (name === 'verifyPrimary' || name === 'affirmPrimary') {
        setEmailVerificationQAState({ primaryState: 'none' })
      } else {
        // addBackup/affirmBackup are the same underlying 'due' toggle, just
        // read differently depending on qa.hasBackupEmail
        setEmailVerificationQAState({ backupState: 'none' })
      }
      if (name === 'addBackup') {
        const addBackupDismissCount = (getEmailVerificationQAState().addBackupDismissCount || 0) + 1
        setEmailVerificationQAState({ addBackupDismissCount })
        patchMockAddBackupDismissCount(dispatch, addBackupDismissCount)
      }
      dispatch({ type: 'DISMISS_EMAIL_VERIFICATION_PROMPT_SUCCESS', name })
    })
  }
  return (dispatch) => api.put(`/users/${userId}/email-verification/prompts/${name}/dismiss`, data)
    .then((response) => {
      dispatch({ type: 'DISMISS_EMAIL_VERIFICATION_PROMPT_SUCCESS', name })
      return response
    })
}

// mirrors the real endpoint: verifyPrimary never needs reauth, updatePrimary/
// upsertBackup do, once per QA "session" (reset via the QA panel or a reload)
function qaCreateEmailVerification(data) {
  const qa = getEmailVerificationQAState()
  const reauthableCredential = data.type && (data.password || data.idToken)
  const needsReauth = data.purpose !== 'verifyPrimary' && qa.requireReauth &&
    !qa.reauthed && !reauthableCredential
  if (needsReauth) {
    return Promise.reject(reauthRequiredError())
  }
  if (reauthableCredential) {
    setEmailVerificationQAState({ reauthed: true })
  }
  return Promise.resolve(fakeVerificationRecord(data.purpose))
}

// data = { purpose: 'verifyPrimary' | 'updatePrimary' | 'upsertBackup', email?,
//          type?: 'password' | 'apple' | 'google', password?, idToken? }
// → { id, purpose, expiresAt, resendAvailableAt }
export function createEmailVerification(userId, data) {
  if (isEmailVerificationQAEnabled()) {
    return () => mockRequestDelay().then(() => qaCreateEmailVerification(data))
  }
  return () => api.post(`/users/${userId}/email-verification`, data)
    .then((response) => response)
}

export function resendEmailVerification(userId, verificationId) {
  if (isEmailVerificationQAEnabled()) {
    return () => mockRequestDelay().then(() => fakeVerificationRecord('resend'))
  }
  return () => api.post(`/users/${userId}/email-verification/${verificationId}/resend`)
    .then((response) => response)
}

// Affirms a previously-verified primary email whose confirmation has gone
// stale (the affirmPrimary prompt) — no code round trip. The caller should
// refetch prompts afterward to see whether addBackup is still due.
export function affirmPrimaryEmail(userId) {
  if (isEmailVerificationQAEnabled()) {
    return () => mockRequestDelay().then(() => {
      setEmailVerificationQAState({ primaryState: 'none' })
      return { status: 'succeeded' }
    })
  }
  return (dispatch) => api.put(`/users/${userId}/email-verification/affirm-primary`)
    .then((response) => {
      // affirm updates meta.emailConfirmedAt, same as verify
      dispatch(fetchUser(userId))
      return response
    })
}

// Affirms a previously-verified backup email whose confirmation has gone
// stale (the affirmBackup prompt) — no code round trip, mirrors affirmPrimary.
export function affirmBackupEmail(userId) {
  if (isEmailVerificationQAEnabled()) {
    return () => mockRequestDelay().then(() => {
      setEmailVerificationQAState({ backupState: 'none' })
      return { status: 'succeeded' }
    })
  }
  return (dispatch) => api.put(`/users/${userId}/email-verification/affirm-backup`)
    .then((response) => {
      dispatch(fetchUser(userId))
      return response
    })
}

// mirrors the real API: updating the primary email to match the current
// backup email retires the backup (see clearBackupEmailInStore's comment)
function qaVerifyEmailVerification(purpose, email, currentBackupEmail) {
  let qa = getEmailVerificationQAState()
  let backupEmailRemoved = false
  if (purpose === 'verifyPrimary' || purpose === 'updatePrimary') {
    backupEmailRemoved = purpose === 'updatePrimary' && !!currentBackupEmail && !!email &&
      email.toLowerCase() === currentBackupEmail.toLowerCase()
    qa = setEmailVerificationQAState({
      primaryState: 'none',
      ...(backupEmailRemoved ? { hasBackupEmail: false, backupEmail: null, backupState: 'none' } : {}),
    })
  } else if (purpose === 'upsertBackup') {
    qa = setEmailVerificationQAState({ hasBackupEmail: true, backupEmail: email || null, backupState: 'none' })
  }
  return {
    status: 'succeeded', attemptsRemaining: null, prompts: derivePrompts(qa), backupEmailRemoved,
  }
}

// purpose: 'verifyPrimary' | 'updatePrimary' | 'upsertBackup'. email is only
// used to keep QA mode's mocked user.email/backupEmail in sync with what was
// actually typed into the form — real requests don't need it (verify is the
// only writer of those fields server-side, and a real fetchUser picks it up).
// → { status, attemptsRemaining, prompts }
export function verifyEmailVerification(userId, verificationId, code, purpose, email) {
  if (isEmailVerificationQAEnabled()) {
    return (dispatch) => mockRequestDelay().then(() => {
      const codeError = mockVerificationCodeError(code)
      if (codeError) {
        return Promise.reject(codeError)
      }
      const currentBackupEmail = store.getState().user && store.getState().user.backupEmail
      const response = qaVerifyEmailVerification(purpose, email, currentBackupEmail)
      dispatch({ type: 'VERIFY_EMAIL_VERIFICATION_SUCCESS', response })
      if (email && (purpose === 'updatePrimary' || purpose === 'upsertBackup')) {
        const patch = purpose === 'updatePrimary' ? { email } : { backupEmail: email }
        dispatch({ type: 'UPDATE_USER_SUCCESS', response: { ...store.getState().user, ...patch } })
      }
      if (response.backupEmailRemoved) {
        clearBackupEmailInStore(dispatch)
      }
      return response
    })
  }
  return (dispatch) => api.put(`/users/${userId}/email-verification/${verificationId}/verify`, { code })
    .then((response) => {
      dispatch({ type: 'VERIFY_EMAIL_VERIFICATION_SUCCESS', response })
      if (response.backupEmailRemoved) {
        clearBackupEmailInStore(dispatch)
      }
      // verify is the only writer of user.email / user.backupEmail / the confirm-verify
      // timestamps, so refresh the user record to pick up whatever changed.
      dispatch(fetchUser(userId))
      return response
    })
}
